import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Link } from "wouter";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { useToast } from "@/hooks/use-toast";
import {
  CheckCircle,
  Star,
  ArrowRight,
  Users,
  FileText,
  Mic,
  Video,
  Globe,
  Newspaper,
  Presentation,
  TrendingUp,
  Award,
  Zap,
  Calendar,
  Download,
  Brain,
  Clock,
  Building2,
  MessageCircle,
  Shield,
  Tag,
  BookOpen,
} from "lucide-react";
import { FaLinkedin } from "react-icons/fa";
import type { MembershipTier } from "@shared/schema";

function formatPriceShort(pence: number | null | undefined): string {
  if (pence === null || pence === undefined) return "POA";
  return `£${(pence / 100).toLocaleString("en-GB", { minimumFractionDigits: 0 })}`;
}

const CHANNELS = [
  { icon: Newspaper, label: "Magazine", description: "Digital & editorial features" },
  { icon: Globe, label: "Website", description: "Online presence & SEO" },
  { icon: FaLinkedin, label: "LinkedIn", description: "34,000 professional network" },
  { icon: Mic, label: "Podcast", description: "Audio thought leadership" },
  { icon: Video, label: "Video", description: "Site-visit & facility film" },
  { icon: Presentation, label: "Exhibitions", description: "Industry event presence" },
  { icon: Users, label: "Keynote Speaking", description: "Expert presentations" },
];

const AUDIENCE_STATS = [
  { value: "34,000+", label: "Verified engineering professionals" },
  { value: "Full chain", label: "Design desks to boardrooms" },
  { value: "UK-first", label: "With significant global reach" },
  { value: "Growing", label: "New members joining every month" },
];

const INDUSTRIES = [
  "Advanced Manufacturing",
  "Automotive & EV",
  "Aerospace & Defence",
  "Electronics",
  "Semiconductors",
  "Environmental Engineering",
  "Industrial Automation",
  "Supply Chain & Logistics",
];

const TRUST_BRANDS = [
  "BAE Systems", "Airbus", "Siemens", "Jaguar Land Rover",
  "JCB", "Dyson", "Arm", "Babcock International",
  "Johnson Matthey", "Leonardo",
];

const MEMBER_BENEFITS = [
  { icon: Brain, title: "Brainstorming Sessions", description: "Regular strategy calls to spark new campaign ideas and align content with your goals." },
  { icon: Clock, title: "Flexible Savings Plan", description: "Monthly direct debit spreads costs instead of a single upfront payment." },
  { icon: FileText, title: "Priority Submissions", description: "Editorial submitted by members is placed at the front of the publishing queue." },
  { icon: FaLinkedin, title: "Executive Visibility", description: "Exclusive LinkedIn promotion of content to our verified professional audience." },
  { icon: Building2, title: "Case Studies", description: "Your success stories showcased in presentations to industry decision-makers." },
  { icon: BookOpen, title: "Copyright-Shared Assets", description: "Articles, videos and podcasts created are yours to reuse freely across your channels." },
  { icon: Shield, title: "Guaranteed Publishing", description: "Relevant press releases are published in the magazine without waiting in the queue." },
  { icon: Tag, title: "Exclusive Partner Discounts", description: "Members-only offers from within our trusted partner network." },
];

const FAQS = [
  {
    q: "What is the minimum contract term?",
    a: "All membership packages run on a minimum 12-month term. Payment is made monthly by direct debit, so your investment is spread across the year rather than paid upfront.",
  },
  {
    q: "What happens after I apply?",
    a: "Once you submit your application, our team will review it and get in touch within 2 business days to confirm your tier, discuss your first high-impact project, and set up your direct debit. You'll be welcomed into the programme as soon as payment is confirmed.",
  },
  {
    q: "Can I cancel or change tier mid-term?",
    a: "Memberships are committed for the minimum 12-month term. However, if your goals change, you can upgrade to a higher tier at any point during your membership. Downgrades take effect at the end of your current term.",
  },
  {
    q: "How does editorial quality work?",
    a: "Our editorial team works collaboratively with you to create content that delivers genuine value to our engineering audience. We use an AI-assisted quality framework (ACT scoring) to ensure every piece meets the editorial standards our readers expect — this is what keeps our audience engaged and trusting.",
  },
  {
    q: "What's the main difference between the membership tiers?",
    a: "Foundation gives you consistent monthly visibility, Hall of Fame placement, and one major high-impact project per year (an editorial/advertising combo). Framework adds three high-impact projects per year including video or podcast options. Vanguard is our premium tier, including a magazine cover feature, 12 monthly editorial placements, four podcast appearances, and top-tier visibility across all channels.",
  },
  {
    q: "Do I own the content you create?",
    a: "Yes. All content — articles, videos, and podcast episodes — created as part of your membership is copyright-shared, meaning you're free to reuse it across your own website, social channels, presentations, and marketing materials.",
  },
];

export default function MembershipPage() {
  const { toast } = useToast();
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    companyName: "",
    contactName: "",
    contactEmail: "",
    contactPhone: "",
    website: "",
    tierInterest: "",
    message: "",
  });

  const { data: tiers = [], isLoading } = useQuery<MembershipTier[]>({
    queryKey: ["/api/public/membership-tiers"],
  });

  const enquiryMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      const res = await fetch("/api/public/membership-enquiry", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || "Failed to submit enquiry");
      }
      return res.json();
    },
    onSuccess: () => {
      setSubmitted(true);
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  const handleEnquirySubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.companyName || !formData.contactName || !formData.contactEmail) {
      toast({ title: "Please fill in required fields", variant: "destructive" });
      return;
    }
    enquiryMutation.mutate(formData);
  };

  const gridClass =
    tiers.length === 1
      ? "max-w-sm mx-auto"
      : tiers.length === 2
      ? "md:grid-cols-2 max-w-3xl mx-auto"
      : "md:grid-cols-3";

  return (
    <>
      <Helmet>
        <title>Membership | Future Manufacturing — B2B Engineering Media Partnership</title>
        <meta
          name="description"
          content="Join Future Manufacturing's B2B membership programme. Reach 34,000 verified engineering professionals across magazine, LinkedIn, podcast, video, and events. Foundation from £155/month."
        />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Membership | Future Manufacturing" />
        <meta
          property="og:description"
          content="Reach 34,000 engineering professionals through editorial, LinkedIn, podcast, video, and events. Choose from Foundation, Framework, or Vanguard membership."
        />
        <meta property="og:url" content="https://futuremfg.com/membership" />
      </Helmet>

      <div className="min-h-screen bg-background">

        {/* ── Hero ──────────────────────────────────────────── */}
        <section className="bg-primary text-primary-foreground py-24 px-4">
          <div className="max-w-5xl mx-auto">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div className="space-y-6">
                <Badge variant="secondary" className="text-sm">B2B Membership Programme</Badge>
                <h1 className="text-4xl md:text-5xl font-bold leading-tight">
                  Where Engineering Innovation Meets Industry Influence
                </h1>
                <p className="text-lg text-primary-foreground/80">
                  Future Manufacturing is the UK's trusted editorial and visibility platform for
                  suppliers who want to reach, influence, and be remembered by the professionals
                  who shape modern industry.
                </p>
                <div className="flex flex-wrap gap-3 pt-2">
                  <Button size="lg" variant="secondary" asChild data-testid="button-hero-apply">
                    <Link href="/membership/apply">
                      Apply for Membership <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </Button>
                  <Button
                    size="lg"
                    variant="outline"
                    className="border-primary-foreground/40 text-primary-foreground hover:bg-primary-foreground/10"
                    asChild
                    data-testid="button-hero-download"
                  >
                    <a
                      href="/attached_assets/Sustainable-Engineering-Alliance-Media-Pack-2026-111225_1776694513482.pdf"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Download className="mr-2 h-4 w-4" />
                      Download Media Pack
                    </a>
                  </Button>
                </div>
              </div>
              <div className="grid grid-cols-1 gap-4">
                {AUDIENCE_STATS.map((stat) => (
                  <div
                    key={stat.label}
                    className="bg-primary-foreground/10 rounded-xl p-5 flex items-center gap-4"
                  >
                    <Users className="h-8 w-8 text-primary-foreground/70 shrink-0" />
                    <div>
                      <div className="text-2xl font-bold">{stat.value}</div>
                      <div className="text-primary-foreground/70 text-sm">{stat.label}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ── Visibility Channels ───────────────────────────── */}
        <section className="py-16 px-4 bg-muted/20 border-b">
          <div className="max-w-5xl mx-auto">
            <p className="text-center text-sm font-medium text-muted-foreground uppercase tracking-wide mb-8">
              Your brand across seven channels
            </p>
            <div className="grid grid-cols-3 md:grid-cols-7 gap-4">
              {CHANNELS.map(({ icon: Icon, label, description }) => (
                <div key={label} className="flex flex-col items-center text-center gap-2">
                  <div className="h-12 w-12 bg-primary/10 rounded-xl flex items-center justify-center">
                    <Icon className="h-6 w-6 text-primary" />
                  </div>
                  <div className="font-semibold text-sm">{label}</div>
                  <div className="text-xs text-muted-foreground hidden md:block">{description}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── Why Choose Us ─────────────────────────────────── */}
        <section className="py-20 px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-14">
              <h2 className="text-3xl font-bold mb-3">Why Partner with Future Manufacturing?</h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                This is not general marketing. It is precision communication tailored for the
                engineering sector, backed by editorial credibility that decision-makers trust.
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-8">
              {[
                {
                  icon: TrendingUp,
                  title: "Targeted Professionals",
                  description:
                    "We reach the exact professionals who specify, select, test, buy, and maintain engineering products — not a broad consumer audience.",
                },
                {
                  icon: Award,
                  title: "Influence Decision-Makers",
                  description:
                    "We influence OEMs, distributors, and engineering managers precisely when they seek trusted information to make purchasing and specification decisions.",
                },
                {
                  icon: Zap,
                  title: "Unique Editorial Credibility",
                  description:
                    "A unique blend of PR, editorial credibility, and targeted digital delivery — including AI-quality scoring — that keeps our audience engaged and returning.",
                },
              ].map(({ icon: Icon, title, description }) => (
                <div key={title} className="text-center space-y-4">
                  <div className="h-14 w-14 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto">
                    <Icon className="h-7 w-7 text-primary" />
                  </div>
                  <h3 className="font-semibold text-lg">{title}</h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">{description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── Industries We Serve ───────────────────────────── */}
        <section className="py-16 px-4 bg-muted/30">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl font-bold text-center mb-8">Industries We Serve</h2>
            <div className="flex flex-wrap gap-3 justify-center">
              {INDUSTRIES.map((sector) => (
                <div
                  key={sector}
                  className="rounded-full border bg-card px-5 py-2 text-sm font-medium"
                >
                  {sector}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── Who Reads Us ──────────────────────────────────── */}
        <section className="py-20 px-4">
          <div className="max-w-5xl mx-auto text-center">
            <p className="text-sm font-medium text-muted-foreground uppercase tracking-wide mb-4">
              Our readers work here
            </p>
            <h2 className="text-3xl font-bold mb-4">Trusted by the Engineering World</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto mb-10">
              Our 34,000-strong professional audience includes readers embedded across some of the
              most respected engineering and technology organisations in the world.
            </p>
            <div className="flex flex-wrap gap-3 justify-center">
              {TRUST_BRANDS.map((brand) => (
                <div
                  key={brand}
                  className="rounded-lg border bg-card px-4 py-2.5 text-sm font-semibold text-muted-foreground"
                >
                  {brand}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── Membership Tiers ──────────────────────────────── */}
        <section id="tiers" className="py-20 px-4 bg-muted/30">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-14">
              <h2 className="text-3xl font-bold mb-3">Membership Packages</h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                Choose the level of partnership that matches your ambitions. All memberships run on
                a minimum 12-month term with monthly direct debit.
              </p>
            </div>

            {isLoading ? (
              <div className="grid md:grid-cols-3 gap-6">
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} className="h-[520px] rounded-xl" />
                ))}
              </div>
            ) : tiers.length === 0 ? (
              <div className="text-center text-muted-foreground py-12 border rounded-xl bg-card">
                <p className="mb-4">Membership packages are being finalised.</p>
                <Button asChild>
                  <a href="#enquiry">Enquire Now</a>
                </Button>
              </div>
            ) : (
              <div className={`grid gap-6 ${gridClass}`}>
                {tiers.map((tier) => (
                  <div
                    key={tier.id}
                    className={`rounded-xl border bg-card p-7 flex flex-col gap-5 relative ${
                      tier.isFeatured
                        ? "border-primary shadow-xl ring-2 ring-primary/20"
                        : "shadow-sm"
                    }`}
                    data-testid={`card-tier-${tier.slug}`}
                  >
                    {tier.isFeatured && (
                      <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
                        <Badge className="bg-primary text-primary-foreground px-4 py-1 text-sm">
                          Most Popular
                        </Badge>
                      </div>
                    )}
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <Star
                          className={`h-4 w-4 ${
                            tier.isFeatured ? "text-primary fill-primary" : "text-muted-foreground"
                          }`}
                        />
                        <h3 className="text-xl font-bold">{tier.name}</h3>
                      </div>
                      <div className="text-3xl font-bold text-primary mb-1">
                        {formatPriceShort(tier.price)}
                        {tier.billingPeriod === "monthly" && (
                          <span className="text-base font-normal text-muted-foreground">/month</span>
                        )}
                        {tier.billingPeriod === "annual" && (
                          <span className="text-base font-normal text-muted-foreground">/year</span>
                        )}
                      </div>
                      {tier.price !== null && (
                        <p className="text-xs text-muted-foreground flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          Minimum 12-month term
                        </p>
                      )}
                      {tier.description && (
                        <p className="text-sm text-muted-foreground mt-3 leading-relaxed">
                          {tier.description}
                        </p>
                      )}
                    </div>
                    <ul className="space-y-2.5 flex-1">
                      {((tier.benefits ?? []) as string[]).map((benefit, idx) => (
                        <li key={idx} className="flex items-start gap-2.5 text-sm">
                          <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                          <span>{benefit}</span>
                        </li>
                      ))}
                    </ul>
                    <Button
                      asChild
                      className="w-full"
                      variant={tier.isFeatured ? "default" : "outline"}
                      data-testid={`button-apply-tier-${tier.slug}`}
                    >
                      <Link href={`/membership/apply?tier=${tier.slug}`}>
                        {tier.price === null ? "Enquire Now" : "Apply for Membership"}
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Link>
                    </Button>
                  </div>
                ))}
              </div>
            )}

            {/* Tier comparison table */}
            {tiers.length > 1 && (
              <div className="mt-12 rounded-xl border bg-card overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-muted/30">
                      <th className="text-left p-4 font-semibold w-48">Feature</th>
                      {tiers.map((tier) => (
                        <th key={tier.id} className="text-center p-4 font-semibold">
                          {tier.name}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      // Slug-based helpers so tier order changes don't break semantics
                      const isFoundation = (t: typeof tiers[number]) =>
                        t.slug === "foundation";
                      const isFramework = (t: typeof tiers[number]) =>
                        t.slug === "framework";
                      const isVanguard = (t: typeof tiers[number]) =>
                        t.slug === "vanguard";
                      const atLeastFramework = (t: typeof tiers[number]) =>
                        isFramework(t) || isVanguard(t);

                      return [
                        { label: "Hall of Fame placement", values: tiers.map(() => true) },
                        { label: "Action Card Pack", values: tiers.map(() => true) },
                        { label: "Priority editorial submissions", values: tiers.map(() => true) },
                        { label: "Copyright-shared assets", values: tiers.map(() => true) },
                        {
                          label: "High-impact projects / year",
                          values: tiers.map((t) =>
                            isFoundation(t) ? "1" : isFramework(t) ? "3" : "12+"
                          ),
                        },
                        {
                          label: "Video content",
                          values: tiers.map((t) => atLeastFramework(t)),
                        },
                        {
                          label: "Podcast appearances",
                          values: tiers.map((t) => atLeastFramework(t)),
                        },
                        {
                          label: "Magazine cover feature",
                          values: tiers.map((t) => isVanguard(t)),
                        },
                        {
                          label: "Newsletter sponsorships",
                          values: tiers.map((t) => isVanguard(t)),
                        },
                      ];
                    })().map(({ label, values }) => (
                      <tr key={label} className="border-b last:border-0 hover:bg-muted/20">
                        <td className="p-4 text-muted-foreground">{label}</td>
                        {values.map((val, i) => (
                          <td key={i} className="p-4 text-center">
                            {typeof val === "boolean" ? (
                              val ? (
                                <CheckCircle className="h-4 w-4 text-green-500 mx-auto" />
                              ) : (
                                <span className="text-muted-foreground/40">—</span>
                              )
                            ) : (
                              <span className="font-medium">{val}</span>
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </section>

        {/* ── Big Splash Projects ───────────────────────────── */}
        <section className="py-20 px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-3">The Big Splash Project Concept</h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                Every membership includes at least one high-impact project per year — a publicity
                asset with enduring value that you can reuse across multiple audiences.
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              {[
                {
                  title: "Ed/Ad Combo",
                  description:
                    "A bespoke technical editorial interview curated by our team, paired with a display A4 advert alongside the story in the magazine. Published digitally, shared on LinkedIn, and copyright-shared for your own use.",
                  tag: "All tiers",
                },
                {
                  title: "Site-Visit Video",
                  description:
                    "Professionally filmed at your facility or an exhibition. We handle scripting to post-production. Uploaded to YouTube with 10–15 short-form video clips created for LinkedIn.",
                  tag: "Framework & Vanguard",
                },
                {
                  title: "World Made Better Podcast",
                  description:
                    "Feature as a guest discussing the subjects that matter to you. Uploaded to Spotify with 10–15 short-form highlights for LinkedIn. Copyright-shared for your wider marketing.",
                  tag: "Framework & Vanguard",
                },
              ].map(({ title, description, tag }) => (
                <div key={title} className="rounded-xl border bg-card p-6 space-y-3">
                  <Badge variant="secondary" className="text-xs">{tag}</Badge>
                  <h3 className="font-semibold text-lg">{title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── Additional Services ───────────────────────────── */}
        <section className="py-20 px-4 bg-muted/30">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-3">Additional Content Services</h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                Beyond core membership, we offer bespoke services to further amplify your brand's
                message and market impact — available to members and non-members alike.
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              {[
                {
                  icon: FileText,
                  title: "Press Release Creation & Distribution",
                  description:
                    "Professionally crafted press releases published across our magazine and distributed through our LinkedIn network.",
                },
                {
                  icon: Globe,
                  title: "Standalone Advertising Campaigns",
                  description:
                    "Premium A4 adverts, exclusive newsletter sponsorships, and targeted LinkedIn distribution campaigns to maximise your reach.",
                },
                {
                  icon: Video,
                  title: "Exhibition Stand Video Interviews",
                  description:
                    "We visit your stand at exhibitions both parties attend, capturing your latest technologies for YouTube and LinkedIn. Free for members.",
                },
                {
                  icon: Mic,
                  title: "Standalone Podcast Episodes",
                  description:
                    "Feature on our podcast as a guest expert — remote or studio recording. Uploaded to Spotify with highlights for LinkedIn. Member rates available.",
                },
                {
                  icon: Presentation,
                  title: "Consultancy & Keynote Speaking",
                  description:
                    "Technical, focused subject seminars and expert keynote speaking engagements tailored to your target audience.",
                },
              ].map(({ icon: Icon, title, description }) => (
                <div key={title} className="flex gap-4 rounded-xl border bg-card p-6">
                  <div className="h-10 w-10 bg-primary/10 rounded-lg flex items-center justify-center shrink-0">
                    <Icon className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">{title}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">{description}</p>
                    <p className="text-xs text-muted-foreground mt-2 italic">Price on application</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── LinkedIn Partnership ──────────────────────────── */}
        <section className="py-20 px-4">
          <div className="max-w-5xl mx-auto">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div className="space-y-5">
                <div className="h-14 w-14 bg-blue-500/10 rounded-2xl flex items-center justify-center">
                  <FaLinkedin className="h-7 w-7 text-blue-600" />
                </div>
                <h2 className="text-3xl font-bold">LinkedIn as a Reach Multiplier</h2>
                <p className="text-muted-foreground leading-relaxed">
                  Engineers increasingly turn to LinkedIn for technical information, learning, and
                  professional discovery. We've built a verified professional network of 34,000+
                  and developed a deliberate LinkedIn strategy that amplifies every piece of content
                  we produce.
                </p>
                <p className="text-muted-foreground leading-relaxed">
                  Every editorial, video, and podcast your membership generates is boosted directly
                  to our LinkedIn audience — and our Action Card Pack places your brand and call to
                  action in front of professionals every month.
                </p>
                <Button variant="outline" asChild data-testid="button-linkedin-enquire">
                  <a href="#enquiry">
                    <MessageCircle className="mr-2 h-4 w-4" />
                    Discuss LinkedIn Strategy
                  </a>
                </Button>
              </div>
              <div className="space-y-4">
                {[
                  "Monthly Action Card Pack distributed to our full LinkedIn audience",
                  "Hall of Fame placement at the front of the magazine every month",
                  "Short-form video highlights from every video and podcast",
                  "Executive LinkedIn promotion of each editorial feature",
                  "Campaign tracking and multichannel reporting",
                ].map((point) => (
                  <div key={point} className="flex items-start gap-3 rounded-lg border bg-card p-4">
                    <CheckCircle className="h-4 w-4 text-blue-500 mt-0.5 shrink-0" />
                    <span className="text-sm">{point}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ── Member Benefits ───────────────────────────────── */}
        <section className="py-20 px-4 bg-muted/30">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-3">Membership Perks</h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                Your membership is more than a subscription — it's a strategic investment in your
                brand's visibility, credibility, and long-term market position.
              </p>
            </div>
            <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-5">
              {MEMBER_BENEFITS.map(({ icon: Icon, title, description }) => (
                <div key={title} className="rounded-xl border bg-card p-5 space-y-3">
                  <div className="h-10 w-10 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Icon className="h-5 w-5 text-primary" />
                  </div>
                  <h3 className="font-semibold text-sm">{title}</h3>
                  <p className="text-xs text-muted-foreground leading-relaxed">{description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── FAQ ───────────────────────────────────────────── */}
        <section className="py-20 px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-12">Frequently Asked Questions</h2>
            <Accordion type="single" collapsible className="space-y-2">
              {FAQS.map((faq, idx) => (
                <AccordionItem
                  key={idx}
                  value={`faq-${idx}`}
                  className="border rounded-xl px-5"
                  data-testid={`accordion-faq-${idx}`}
                >
                  <AccordionTrigger className="text-left font-medium py-4">
                    {faq.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed pb-4">
                    {faq.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* ── Inline Enquiry Form ───────────────────────────── */}
        <section id="enquiry" className="py-20 px-4 bg-muted/30">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-3xl font-bold mb-3">Get in Touch</h2>
              <p className="text-muted-foreground">
                Interested in membership but have questions? Fill in the form below and our team
                will come back to you within 2 business days.
              </p>
            </div>

            {submitted ? (
              <div
                className="rounded-xl border bg-card p-10 text-center space-y-4"
                data-testid="enquiry-success"
              >
                <CheckCircle className="h-12 w-12 text-green-500 mx-auto" />
                <h3 className="text-xl font-semibold">Enquiry Received</h3>
                <p className="text-muted-foreground">
                  Thank you for your interest in Future Manufacturing membership. We'll be in touch
                  within 2 business days.
                </p>
                <Button variant="outline" onClick={() => setSubmitted(false)}>
                  Send Another Enquiry
                </Button>
              </div>
            ) : (
              <form
                onSubmit={handleEnquirySubmit}
                className="rounded-xl border bg-card p-8 space-y-5"
              >
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium" htmlFor="contactName">
                      Your Name <span className="text-destructive">*</span>
                    </label>
                    <Input
                      id="contactName"
                      value={formData.contactName}
                      onChange={(e) => setFormData(f => ({ ...f, contactName: e.target.value }))}
                      placeholder="Jane Smith"
                      required
                      data-testid="input-enquiry-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium" htmlFor="companyName">
                      Company <span className="text-destructive">*</span>
                    </label>
                    <Input
                      id="companyName"
                      value={formData.companyName}
                      onChange={(e) => setFormData(f => ({ ...f, companyName: e.target.value }))}
                      placeholder="Acme Engineering Ltd"
                      required
                      data-testid="input-enquiry-company"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium" htmlFor="contactEmail">
                      Email <span className="text-destructive">*</span>
                    </label>
                    <Input
                      id="contactEmail"
                      type="email"
                      value={formData.contactEmail}
                      onChange={(e) => setFormData(f => ({ ...f, contactEmail: e.target.value }))}
                      placeholder="jane@acme.com"
                      required
                      data-testid="input-enquiry-email"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium" htmlFor="contactPhone">
                      Phone
                    </label>
                    <Input
                      id="contactPhone"
                      type="tel"
                      value={formData.contactPhone}
                      onChange={(e) => setFormData(f => ({ ...f, contactPhone: e.target.value }))}
                      placeholder="+44 7700 900000"
                      data-testid="input-enquiry-phone"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium" htmlFor="website">
                    Website
                  </label>
                  <Input
                    id="website"
                    type="url"
                    value={formData.website}
                    onChange={(e) => setFormData(f => ({ ...f, website: e.target.value }))}
                    placeholder="https://www.acme.com"
                    data-testid="input-enquiry-website"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Tier of Interest</label>
                  <Select
                    value={formData.tierInterest}
                    onValueChange={(val) => setFormData(f => ({ ...f, tierInterest: val }))}
                  >
                    <SelectTrigger data-testid="select-enquiry-tier">
                      <SelectValue placeholder="Select a membership tier" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="foundation">Foundation</SelectItem>
                      <SelectItem value="framework">Framework</SelectItem>
                      <SelectItem value="vanguard">Vanguard</SelectItem>
                      <SelectItem value="not-sure">Not sure yet</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium" htmlFor="message">
                    Message
                  </label>
                  <Textarea
                    id="message"
                    value={formData.message}
                    onChange={(e) => setFormData(f => ({ ...f, message: e.target.value }))}
                    placeholder="Tell us about your goals, target audience, or any questions you have…"
                    rows={4}
                    data-testid="input-enquiry-message"
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full"
                  disabled={enquiryMutation.isPending}
                  data-testid="button-enquiry-submit"
                >
                  {enquiryMutation.isPending ? "Sending…" : "Send Enquiry"}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </form>
            )}
          </div>
        </section>

        {/* ── Final CTA ─────────────────────────────────────── */}
        <section className="py-20 px-4 bg-primary text-primary-foreground">
          <div className="max-w-3xl mx-auto text-center space-y-6">
            <h2 className="text-3xl font-bold">Ready to Lead the Conversation?</h2>
            <p className="text-primary-foreground/80 text-lg">
              Join the Future Manufacturing membership programme and ensure your brand is seen,
              heard, and remembered by the professionals who matter most.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Button size="lg" variant="secondary" asChild data-testid="button-cta-apply">
                <Link href="/membership/apply">
                  Apply for Membership <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="border-primary-foreground/40 text-primary-foreground hover:bg-primary-foreground/10"
                asChild
                data-testid="button-cta-enquire"
              >
                <a href="#enquiry">
                  <MessageCircle className="mr-2 h-4 w-4" />
                  Enquire Now
                </a>
              </Button>
            </div>
            <p className="text-sm text-primary-foreground/60">
              Minimum 12-month term · Monthly direct debit · Cancel with notice
            </p>
          </div>
        </section>

      </div>
    </>
  );
}
